/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { TravelPremiumsData } from '../../types/travelInsurance';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatNumberWithSeparator } from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import Spinner from '../Spinner';
import { TravelInsuranceCtaType } from '../TravelInsuranceCtaBuilder';
import { TRANSLATIONS } from './i18n';

type SectionTravelInsuranceProps = {
  travelInsuranceQuotes?: TravelPremiumsData[0];
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
};

export const SectionTravelInsurance: React.FC<SectionTravelInsuranceProps> = ({
  travelInsuranceQuotes,
  travelInsuranceCtaComponent,
}) => {
  const { lang, brand } = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, { brand, lang });

  const { currencySymbol, offeredPremium, originalPremium } =
    travelInsuranceQuotes || {};
  const offeredPremiumNum = offeredPremium ? parseFloat(offeredPremium) : 0;
  const originalPremiumNum = originalPremium ? parseFloat(originalPremium) : 0;

  const discount =
    originalPremiumNum > 0
      ? Math.round(
          ((originalPremiumNum - offeredPremiumNum) / originalPremiumNum) * 100,
        )
      : 0;

  const hasPremium = offeredPremiumNum > 0;
  const hasDiscount = discount > 0;
  const isFetchingPrice = offeredPremium === '' && originalPremium === '';

  const renderPrice = (variant: 'mobile' | 'desktop') => {
    const isMobile = variant === 'mobile';

    return (
      <div
        className={classNames('w-full max-w-general/2-col-width', {
          'block md:hidden': isMobile,
          'hidden md:flex md:justify-center': !isMobile,
          'md:text-center': !isFetchingPrice && !isMobile,
        })}
      >
        {isFetchingPrice && (
          <div className="mb-spacing/gap/soft-8px flex justify-end gap-spacing/gap/soft-8px">
            <Spinner className="inline size-6" />
            <span>{tr('fetch_price', staticText)}...</span>
          </div>
        )}
        {hasPremium && (
          <div
            className={classNames('flex items-baseline gap-[2px]', {
              'justify-start text-left': isMobile,
              'justify-center text-center h-form/chip-min-height': !isMobile,
            })}
          >
            <span className="mhg-alpha-caption">{currencySymbol}</span>
            <span className="mhg-alpha-title-1">
              {formatNumberWithSeparator({
                value: offeredPremiumNum,
                thousandsSeparator: ',',
                decimalSeparator: '.',
                alwaysTwoDecimals: true,
              })}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderOriginalWithDiscount = (variant: 'mobile' | 'desktop') => {
    const isMobile = variant === 'mobile';

    return (
      <div
        className={classNames('w-full max-w-general/2-col-width', {
          'block md:hidden': isMobile,
          'hidden md:flex md:justify-center md:text-center': !isMobile,
        })}
      >
        {hasDiscount && (
          <div
            className={classNames(
              'flex items-center gap-spacing/gap/soft-6px',
              {
                'justify-start text-left': isMobile,
                'justify-center text-center h-[--button-size-small]': !isMobile,
              },
            )}
          >
            <span className="font-font-weight/table-cell-normal line-through text-text/secondary-lighter text-font-size/table-cell leading-line-height/table-cell">
              {currencySymbol}
              {formatNumberWithSeparator({
                value: originalPremiumNum,
                thousandsSeparator: ',',
                decimalSeparator: '.',
                alwaysTwoDecimals: true,
              })}
            </span>

            <div className="text-nowrap rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px bg-highlights/green-light text-highlights/green-dark">
              <span className="font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell">
                {lang === 'zh' ? tr('off', staticText) : ''}
                {discount}% {lang === 'zh' ? '' : tr('off', staticText)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="block md:hidden">
        {renderPrice('mobile')}
        {renderOriginalWithDiscount('mobile')}
      </div>

      <div className="w-full max-w-general/2-col-width md:flex md:flex-col md:items-center">
        {renderPrice('desktop')}
        {travelInsuranceCtaComponent &&
          travelInsuranceCtaComponent('productCard')}
        <div className="hidden w-full md:block">
          {renderOriginalWithDiscount('desktop')}
        </div>
      </div>
    </div>
  );
};
